import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, Star, Crown, Lock, Play, TrendingUp } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { prestigeService } from '../../services/prestigeService';
import NavigationBar from '../../components/ui/NavigationBar';
import Button from '../../components/ui/Button';
import PrestigeBadge from '../../components/PrestigeBadge';
import LoadingState from '../../components/ui/LoadingState';
import { cn } from '../../utils/cn';

/**
 * PrestigeDrillsPage - Browse and access unlocked prestige drills
 * Shows locked drills for future prestige levels as incentive
 */
const PrestigeDrillsPage = () => {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [unlockedDrills, setUnlockedDrills] = useState([]);
  const [allPrestigeDrills, setAllPrestigeDrills] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/signin');
      return;
    }
    loadPrestigeDrills();
  }, [user]);

  const loadPrestigeDrills = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get user's unlocked prestige drills
      const unlocked = await prestigeService.getPrestigeDrills(user.id);
      setUnlockedDrills(unlocked);

      // Get all prestige drills to show locked ones
      // This would require a new service method or querying directly
      // For now, we'll just show unlocked drills
      setAllPrestigeDrills(unlocked);
    } catch (err) {
      console.error('Failed to load prestige drills:', err);
      setError('Failed to load prestige drills');
    } finally {
      setLoading(false);
    }
  };

  const getPrestigeLevelInfo = (prestigeLevel) => {
    const config = {
      1: { name: 'Bronze Prestige', color: '#CD7F32', icon: Trophy },
      2: { name: 'Silver Prestige', color: '#C0C0C0', icon: Star },
      3: { name: 'Gold Prestige', color: '#FFD700', icon: Crown },
    };
    return config[prestigeLevel] || config[1];
  };

  const handleStartDrill = (drill) => {
    // Navigate to daily drill page with this prestige drill
    navigate('/app/daily-drill', { state: { prestigeDrill: drill } });
  };

  if (loading) {
    return <LoadingState />;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <NavigationBar />

      <main className="container mx-auto px-4 py-8 pb-24 md:pb-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl flex items-center justify-center">
              <Trophy size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Elite Prestige Drills
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Exclusive high-difficulty drills for prestigious players
              </p>
            </div>
          </div>

          {/* User's Prestige Status */}
          {profile?.prestige_level > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Your Prestige:</span>
                  <PrestigeBadge prestigeLevel={profile.prestige_level} size="md" />
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {unlockedDrills.length} Elite Drills Unlocked
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <p className="text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {/* No Prestige Yet */}
        {profile?.prestige_level === 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-8 text-center border border-gray-200 dark:border-gray-700">
            <Lock size={48} className="text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Prestige Drills Locked
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
              Reach Level 50 and prestige to unlock exclusive elite drills designed for advanced players.
            </p>
            <Button
              onClick={() => navigate('/gamification')}
              className="bg-gradient-to-r from-purple-600 to-indigo-600"
            >
              <TrendingUp size={18} className="mr-2" />
              View Progress
            </Button>
          </div>
        )}

        {/* Unlocked Drills */}
        {unlockedDrills.length > 0 && (
          <div className="space-y-6">
            {/* Group drills by prestige level */}
            {[1, 2, 3].map((prestigeLevel) => {
              const levelDrills = unlockedDrills.filter(
                (drill) => drill.prestige_level === prestigeLevel
              );
              
              if (levelDrills.length === 0) return null;

              const levelInfo = getPrestigeLevelInfo(prestigeLevel);
              const LevelIcon = levelInfo.icon;

              return (
                <div key={prestigeLevel}>
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: levelInfo.color + '20' }}
                    >
                      <LevelIcon size={20} style={{ color: levelInfo.color }} />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {levelInfo.name} Drills
                    </h2>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {levelDrills.map((drill) => (
                      <div
                        key={drill.id}
                        className="bg-white dark:bg-gray-800 rounded-lg p-5 border border-gray-200 dark:border-gray-700 hover:border-purple-500 dark:hover:border-purple-400 transition-colors"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <h3 className="font-semibold text-gray-900 dark:text-white">
                            {drill.title}
                          </h3>
                          <PrestigeBadge
                            prestigeLevel={drill.prestige_level}
                            size="xs"
                            showLabel={false}
                          />
                        </div>
                        
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-3">
                          {drill.description}
                        </p>

                        <div className="flex items-center justify-between">
                          <span className="text-xs px-2 py-1 bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-200 rounded font-medium">
                            Elite Difficulty
                          </span>
                          <Button
                            size="sm"
                            onClick={() => handleStartDrill(drill)}
                            className="bg-gradient-to-r from-purple-600 to-indigo-600"
                          >
                            <Play size={14} className="mr-1" />
                            Start
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {unlockedDrills.length === 0 && profile?.prestige_level > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-8 text-center border border-gray-200 dark:border-gray-700">
            <Trophy size={48} className="text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              No Prestige Drills Available
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              There was an error loading your prestige drills. Please try refreshing the page.
            </p>
          </div>
        )}
      </main></div>
  );
};

export default PrestigeDrillsPage;
